import { useState, useEffect, useMemo } from 'react';
import type { Project } from '../data/projects';
import type { UserResponse } from '../services/users';
import * as tasksAPI from '../services/tasks';
import type { CreateTaskPayload, ConflictInfo, RescheduleTaskChange } from '../services/tasks';
import { getProjects, getProjectDisplayName } from '../data/projects';
import { useAuth } from '../services/auth';

type TaskCreateModalProps = {
	projectId?: string; // Optional: if provided, pre-select this project
	gardeners: UserResponse[];
	onClose: () => void;
	onSuccess: () => void; // Callback after successful creation
	/** Tasks page: all-companies mode — combined with multi-company user, requires picking a company before projects */
	multiCompanyCreate?: boolean;
	requesterUserId?: number;
};

export default function TaskCreateModal({
	projectId: initialProjectId,
	gardeners,
	onClose,
	onSuccess,
	multiCompanyCreate = false,
	requesterUserId
}: TaskCreateModalProps) {
	const { user } = useAuth();
	const [isSaving, setIsSaving] = useState(false);
	const [projects, setProjects] = useState<Project[]>([]);
	const [selectedProjectId, setSelectedProjectId] = useState<string>(initialProjectId || '');
	const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');

	const needsCompanyPicker = useMemo(() => {
		if (initialProjectId) return false;
		return Boolean(
			multiCompanyCreate &&
				requesterUserId != null &&
				!Number.isNaN(requesterUserId) &&
				(user?.companyIds?.length ?? 0) > 1
		);
	}, [initialProjectId, multiCompanyCreate, requesterUserId, user?.companyIds]);

	const companyOptions = useMemo(() => {
		const m = new Map<string, string>();
		for (const p of projects) {
			if (p.companyId) {
				const label = p.companyName?.trim() || `Company #${p.companyId}`;
				m.set(p.companyId, label);
			}
		}
		return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1]));
	}, [projects]);

	const projectsForSelect = useMemo(() => {
		if (!needsCompanyPicker) return projects;
		if (!selectedCompanyId) return [];
		return projects.filter(p => p.companyId === selectedCompanyId);
	}, [needsCompanyPicker, selectedCompanyId, projects]);
	const [title, setTitle] = useState('');
	const [description, setDescription] = useState('');
	const [type, setType] = useState<'task' | 'milestone' | 'phase'>('task');
	const [status, setStatus] = useState<'not_started' | 'in_progress' | 'completed' | 'blocked' | 'at_risk' | 'delayed'>('not_started');
	const [startDate, setStartDate] = useState('');
	const [endDate, setEndDate] = useState('');
	const [dueDate, setDueDate] = useState('');
	const [selectedGardeners, setSelectedGardeners] = useState<string[]>([]);
	const [isCritical, setIsCritical] = useState(false);
	const [notes, setNotes] = useState('');
	const [conflicts, setConflicts] = useState<ConflictInfo[]>([]);
	const [isCheckingConflicts, setIsCheckingConflicts] = useState(false);
	const [ignoreConflicts, setIgnoreConflicts] = useState(false);
	const [rescheduleProposal, setRescheduleProposal] = useState<RescheduleTaskChange[]>([]);
	const [isCalculatingReschedule, setIsCalculatingReschedule] = useState(false);
	const [applyAutoReschedule, setApplyAutoReschedule] = useState(false);

	// Load projects
	useEffect(() => {
		const loadProjects = async () => {
			try {
				let loadedProjects: Project[];
				const reqId = requesterUserId;
				const canScope =
					(user?.role === 'company' || user?.role === 'gardener') &&
					reqId != null &&
					!Number.isNaN(reqId);

				if (initialProjectId) {
					if (user?.role === 'company' || user?.role === 'admin') {
						loadedProjects = await getProjects();
					} else if (user?.role === 'gardener' && user?.id) {
						loadedProjects = await getProjects(parseInt(String(user.id), 10));
					} else {
						loadedProjects = await getProjects();
					}
				} else if (canScope) {
					loadedProjects = await getProjects({
						requester_user_id: reqId,
						company_scope: needsCompanyPicker || multiCompanyCreate ? 'all_companies' : 'company'
					});
				} else if (user?.role === 'company' || user?.role === 'admin') {
					loadedProjects = await getProjects();
				} else if (user?.role === 'gardener' && user?.id) {
					loadedProjects = await getProjects(parseInt(String(user.id), 10));
				} else {
					loadedProjects = await getProjects();
				}
				setProjects(loadedProjects);
			} catch (err) {
				console.error('Failed to load projects:', err);
				setProjects([]);
			}
		};
		loadProjects();
	}, [user, initialProjectId, multiCompanyCreate, requesterUserId, needsCompanyPicker]);

	useEffect(() => {
		if (!needsCompanyPicker || !selectedCompanyId) return;
		const stillValid = projects.some(
			p => String(p.id) === String(selectedProjectId) && p.companyId === selectedCompanyId
		);
		if (!stillValid) setSelectedProjectId('');
	}, [needsCompanyPicker, selectedCompanyId, projects, selectedProjectId]);

	// Set default dates
	useEffect(() => {
		const today = new Date();
		const tomorrow = new Date(today);
		tomorrow.setDate(tomorrow.getDate() + 1);
		const nextWeek = new Date(today);
		nextWeek.setDate(nextWeek.getDate() + 7);

		if (!startDate) {
			setStartDate(today.toISOString().split('T')[0]);
		}
		if (!endDate) {
			setEndDate(nextWeek.toISOString().split('T')[0]);
		}
		if (!dueDate) {
			setDueDate(nextWeek.toISOString().split('T')[0]);
		}
	}, []);

	// Check for conflicts when dates or project change
	useEffect(() => {
		if (selectedProjectId && startDate && endDate && startDate <= endDate) {
			checkConflicts();
			calculateReschedule();
		} else {
			setConflicts([]);
			setRescheduleProposal([]);
		}
	}, [selectedProjectId, startDate, endDate]);

	const checkConflicts = async () => {
		if (!selectedProjectId || !startDate || !endDate) return;
		
		setIsCheckingConflicts(true);
		setIgnoreConflicts(false);
		try {
			const result = await tasksAPI.checkTaskConflicts({
				project_id: parseInt(selectedProjectId),
				start_date: new Date(startDate).toISOString(),
				end_date: new Date(endDate).toISOString()
			});
			setConflicts(result.conflicts || []);
		} catch (err) {
			console.error('Failed to check conflicts:', err);
			// Don't show error to user, just silently fail
			setConflicts([]);
		} finally {
			setIsCheckingConflicts(false);
		}
	};

	const calculateReschedule = async () => {
		if (!selectedProjectId || !startDate || !endDate) return;
		
		setIsCalculatingReschedule(true);
		try {
			const result = await tasksAPI.calculateAutoReschedule({
				project_id: parseInt(selectedProjectId),
				start_date: new Date(startDate).toISOString(),
				end_date: new Date(endDate).toISOString(),
				reschedule_mode: 'shift_forward'
			});
			setRescheduleProposal(result.affected_tasks || []);
		} catch (err) {
			console.error('Failed to calculate reschedule:', err);
			setRescheduleProposal([]);
		} finally {
			setIsCalculatingReschedule(false);
		}
	};

	const toggleGardener = (gardenerId: string) => {
		setSelectedGardeners(prev =>
			prev.includes(gardenerId)
				? prev.filter(id => id !== gardenerId)
				: [...prev, gardenerId]
		);
	};

	const handleSubmit = async () => {
		if (!title.trim()) {
			alert('Please enter a task title');
			return;
		}

		if (needsCompanyPicker && !selectedCompanyId) {
			alert('Please select which company this task belongs to');
			return;
		}

		if (!selectedProjectId) {
			alert('Please select a project');
			return;
		}

		if (!startDate || !endDate) {
			alert('Please select both start and end dates');
			return;
		}

		// Check for conflicts if not already ignored and not applying auto-reschedule
		if (!ignoreConflicts && !applyAutoReschedule && conflicts.length > 0) {
			const proceed = confirm(
				`Warning: This task overlaps with ${conflicts.length} other task(s). ` +
				`This may cause scheduling conflicts. Do you want to proceed anyway?`
			);
			if (!proceed) {
				return;
			}
		}

		setIsSaving(true);
		try {
			// Calculate duration in days
			const start = new Date(startDate);
			const end = new Date(endDate);
			const duration = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

			const payload: CreateTaskPayload = {
				project_id: parseInt(selectedProjectId),
				title: title.trim(),
				description: description.trim() || undefined,
				type,
				status,
				start_date: new Date(startDate).toISOString(),
				end_date: new Date(endDate).toISOString(),
				due_date: dueDate ? new Date(dueDate).toISOString() : undefined,
				duration,
				progress: status === 'completed' ? 100 : 0,
				is_critical: isCritical,
				gardener_ids: selectedGardeners.map(id => parseInt(id)),
				notes: notes.trim() || undefined
			};

			// Create the task first
			await tasksAPI.createTask(payload);

			// Apply auto-rescheduling if requested
			if (applyAutoReschedule && rescheduleProposal.length > 0) {
				try {
					await tasksAPI.applyAutoReschedule({
						task_changes: rescheduleProposal,
						reason: `Auto-rescheduled due to insertion of task: ${title.trim()}`
					});
				} catch (rescheduleErr) {
					console.error('Failed to apply auto-reschedule:', rescheduleErr);
					// Don't fail the whole operation, just log the error
					alert('Task created successfully, but auto-rescheduling failed. Please reschedule manually.');
				}
			}

			onSuccess();
			onClose();
		} catch (err) {
			console.error('Failed to create task:', err);
			alert(`Failed to create task: ${err instanceof Error ? err.message : 'Unknown error'}`);
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<div
			className="modal show d-block"
			style={{
				backgroundColor: 'rgba(0,0,0,0.5)',
				// Ensure the backdrop/modal is always above the public fixed header.
				zIndex: 20000,
				position: 'fixed',
				inset: 0,
				overflowY: 'auto'
			}}
			onClick={onClose}
		>
			<div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
				<div className="modal-content">
					<div className="modal-header">
						<h5 className="modal-title">Create New Task</h5>
						<button type="button" className="btn-close" onClick={onClose} disabled={isSaving}></button>
					</div>
					<div className="modal-body">
						{needsCompanyPicker && (
							<div className="mb-3">
								<label className="form-label">
									Company <span className="text-danger">*</span>
								</label>
								<select
									className="form-select"
									value={selectedCompanyId}
									onChange={e => {
										setSelectedCompanyId(e.target.value);
										setSelectedProjectId('');
									}}
									disabled={isSaving}
								>
									<option value="">Select company…</option>
									{companyOptions.map(([id, name]) => (
										<option key={id} value={id}>
											{name}
										</option>
									))}
								</select>
								<small className="text-muted">Choose the company this task is for, then pick a project.</small>
							</div>
						)}

						{/* Project Selection */}
						<div className="mb-3">
							<label className="form-label">
								Project <span className="text-danger">*</span>
							</label>
							<select
								className="form-select"
								value={selectedProjectId}
								onChange={(e) => setSelectedProjectId(e.target.value)}
								disabled={isSaving || !!initialProjectId || (needsCompanyPicker && !selectedCompanyId)}
							>
								<option value="">
									{needsCompanyPicker && !selectedCompanyId
										? 'Select a company first…'
										: 'Select a project...'}
								</option>
								{projectsForSelect.map(project => (
									<option key={project.id} value={project.id}>
										{getProjectDisplayName(project)}
										{project.companyName ? ` · ${project.companyName}` : ''}
									</option>
								))}
							</select>
							{initialProjectId && (
								<small className="text-muted">Project is pre-selected from context</small>
							)}
						</div>

						{/* Title */}
						<div className="mb-3">
							<label className="form-label">
								Task Title <span className="text-danger">*</span>
							</label>
							<input
								type="text"
								className="form-control"
								value={title}
								onChange={(e) => setTitle(e.target.value)}
								placeholder="Enter task title..."
								disabled={isSaving}
							/>
						</div>

						{/* Description */}
						<div className="mb-3">
							<label className="form-label">Description</label>
							<textarea
								className="form-control"
								rows={3}
								value={description}
								onChange={(e) => setDescription(e.target.value)}
								placeholder="Enter task description..."
								disabled={isSaving}
							/>
						</div>

						{/* Type and Status */}
						<div className="row g-3 mb-3">
							<div className="col-md-6">
								<label className="form-label">Type</label>
								<select
									className="form-select"
									value={type}
									onChange={(e) => setType(e.target.value as 'task' | 'milestone' | 'phase')}
									disabled={isSaving}
								>
									<option value="task">Task</option>
									<option value="milestone">Milestone</option>
									<option value="phase">Phase</option>
								</select>
							</div>
							<div className="col-md-6">
								<label className="form-label">Status</label>
								<select
									className="form-select"
									value={status}
									onChange={(e) => setStatus(e.target.value as typeof status)}
									disabled={isSaving}
								>
									<option value="not_started">Not Started</option>
									<option value="in_progress">In Progress</option>
									<option value="completed">Completed</option>
									<option value="at_risk">At Risk</option>
									<option value="delayed">Delayed</option>
									<option value="blocked">Blocked</option>
								</select>
							</div>
						</div>

						{/* Dates */}
						<div className="row g-3 mb-3">
							<div className="col-md-4">
								<label className="form-label">
									Start Date <span className="text-danger">*</span>
								</label>
								<input
									type="date"
									className="form-control"
									value={startDate}
									onChange={(e) => setStartDate(e.target.value)}
									disabled={isSaving}
								/>
							</div>
							<div className="col-md-4">
								<label className="form-label">
									End Date <span className="text-danger">*</span>
								</label>
								<input
									type="date"
									className="form-control"
									value={endDate}
									onChange={(e) => setEndDate(e.target.value)}
									disabled={isSaving}
								/>
							</div>
							<div className="col-md-4">
								<label className="form-label">Due Date</label>
								<input
									type="date"
									className="form-control"
									value={dueDate}
									onChange={(e) => setDueDate(e.target.value)}
									disabled={isSaving}
								/>
							</div>
						</div>

						{/* Conflict Warning */}
						{isCheckingConflicts && (
							<div className="alert alert-info mb-3">
								<i className="bi bi-hourglass-split me-2"></i>
								Checking for scheduling conflicts...
							</div>
						)}
						{!isCheckingConflicts && conflicts.length > 0 && (
							<div className="alert alert-warning mb-3">
								<div className="d-flex align-items-start">
									<i className="bi bi-exclamation-triangle-fill me-2 mt-1"></i>
									<div className="flex-grow-1">
										<strong>Schedule Conflict Detected!</strong>
										<p className="mb-2">
											This task overlaps with {conflicts.length} other task(s) in this project:
										</p>
										<ul className="mb-2">
											{conflicts.map((conflict) => (
												<li key={conflict.id}>
													<strong>{conflict.title}</strong> ({conflict.status})
													<br />
													<small className="text-muted">
														{new Date(conflict.start_date).toLocaleDateString()} - {new Date(conflict.end_date).toLocaleDateString()}
														{conflict.gardener_names.length > 0 && (
															<> • Assigned to: {conflict.gardener_names.join(', ')}</>
														)}
													</small>
												</li>
											))}
										</ul>
									</div>
								</div>
							</div>
						)}

						{/* Auto-Reschedule Proposal */}
						{isCalculatingReschedule && (
							<div className="alert alert-info mb-3">
								<i className="bi bi-hourglass-split me-2"></i>
								Calculating auto-reschedule options...
							</div>
						)}
						{!isCalculatingReschedule && rescheduleProposal.length > 0 && (
							<div className="alert alert-success mb-3">
								<div className="d-flex align-items-start">
									<i className="bi bi-calendar-check me-2 mt-1"></i>
									<div className="flex-grow-1">
										<strong>Auto-Reschedule Available!</strong>
										<p className="mb-2">
											We can automatically reschedule {rescheduleProposal.length} affected task(s) to make room for this task:
										</p>
										<div className="table-responsive mb-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
											<table className="table table-sm table-bordered">
												<thead>
													<tr>
														<th>Task</th>
														<th>Old Dates</th>
														<th>New Dates</th>
														<th>Shift</th>
													</tr>
												</thead>
												<tbody>
													{rescheduleProposal.map((change) => (
														<tr key={change.task_id}>
															<td><strong>{change.task_title}</strong></td>
															<td>
																<small>
																	{new Date(change.old_start_date).toLocaleDateString()} - {new Date(change.old_end_date).toLocaleDateString()}
																</small>
															</td>
															<td>
																<small className="text-success">
																	{new Date(change.new_start_date).toLocaleDateString()} - {new Date(change.new_end_date).toLocaleDateString()}
																</small>
															</td>
															<td>
																<small className="text-muted">
																	{change.shift_days > 0 ? '+' : ''}{change.shift_days} days
																</small>
															</td>
														</tr>
													))}
												</tbody>
											</table>
										</div>
										<div className="form-check">
											<input
												className="form-check-input"
												type="checkbox"
												id="applyAutoReschedule"
												checked={applyAutoReschedule}
												onChange={(e) => {
													setApplyAutoReschedule(e.target.checked);
													if (e.target.checked) {
														setIgnoreConflicts(false);
													}
												}}
											/>
											<label className="form-check-label" htmlFor="applyAutoReschedule">
												<strong>Apply auto-rescheduling</strong> - Automatically shift affected tasks when creating this task
											</label>
										</div>
										{!applyAutoReschedule && (
											<div className="form-check mt-2">
												<input
													className="form-check-input"
													type="checkbox"
													id="ignoreConflicts"
													checked={ignoreConflicts}
													onChange={(e) => {
														setIgnoreConflicts(e.target.checked);
														if (e.target.checked) {
															setApplyAutoReschedule(false);
														}
													}}
												/>
												<label className="form-check-label" htmlFor="ignoreConflicts">
													I understand the conflict and want to proceed anyway (no auto-rescheduling)
												</label>
											</div>
										)}
									</div>
								</div>
							</div>
						)}
						{!isCalculatingReschedule && rescheduleProposal.length === 0 && conflicts.length > 0 && (
							<div className="form-check mb-3">
								<input
									className="form-check-input"
									type="checkbox"
									id="ignoreConflictsFallback"
									checked={ignoreConflicts}
									onChange={(e) => setIgnoreConflicts(e.target.checked)}
								/>
								<label className="form-check-label" htmlFor="ignoreConflictsFallback">
									I understand the conflict and want to proceed anyway
								</label>
							</div>
						)}

						{/* Gardeners */}
						<div className="mb-3">
							<label className="form-label">Assign Gardeners</label>
							<div className="border rounded p-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
								{gardeners.length > 0 ? (
									gardeners.map((gardener) => (
										<div key={gardener.id} className="form-check">
											<input
												className="form-check-input"
												type="checkbox"
												id={`create-gardener-${gardener.id}`}
												checked={selectedGardeners.includes(String(gardener.id))}
												onChange={() => toggleGardener(String(gardener.id))}
												disabled={isSaving}
											/>
											<label className="form-check-label" htmlFor={`create-gardener-${gardener.id}`}>
												{gardener.name} {gardener.role && `(${gardener.role})`}
											</label>
										</div>
									))
								) : (
									<p className="text-muted small mb-0">No gardeners available</p>
								)}
							</div>
						</div>

						{/* Critical Path */}
						<div className="mb-3">
							<div className="form-check">
								<input
									className="form-check-input"
									type="checkbox"
									id="isCritical"
									checked={isCritical}
									onChange={(e) => setIsCritical(e.target.checked)}
									disabled={isSaving}
								/>
								<label className="form-check-label" htmlFor="isCritical">
									Critical Path Task
								</label>
							</div>
						</div>

						{/* Notes */}
						<div className="mb-3">
							<label className="form-label">Notes</label>
							<textarea
								className="form-control"
								rows={3}
								value={notes}
								onChange={(e) => setNotes(e.target.value)}
								placeholder="Add any additional notes..."
								disabled={isSaving}
							/>
						</div>
					</div>
					<div className="modal-footer">
						<button
							type="button"
							className="btn btn-secondary"
							onClick={onClose}
							disabled={isSaving}
						>
							Cancel
						</button>
						<button
							type="button"
							className="btn btn-success"
							onClick={handleSubmit}
							disabled={isSaving}
						>
							{isSaving ? (
								<>
									<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
									Creating...
								</>
							) : (
								<>
									<i className="bi bi-plus-circle me-2"></i>Create Task
								</>
							)}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}

